<script setup lang="ts">
import { computed } from 'vue'

import { usePageStore } from '../../../../stores/page'
import { useEpgWidgetTracking } from '../../../../composables/use-epg-widget-tracking'
import BrandTeaser from '../BrandTeaser.vue'

import type { EpgWidgetParagraph } from '../../../../utils/paragraph-schema/paragraphs'

type EpgWidget = EpgWidgetParagraph['epgWidget']
type EpgResultSet = NonNullable<EpgWidget['initialBroadcasts']>[number]
type Channel = EpgResultSet['channel']
type Broadcast = EpgResultSet['broadcasts'][number]

const props = defineProps<{
  broadcast: Broadcast,
  channel: Channel,
  // how many broadcasts share this card's grid row (1/2/3+) - determines which image srcSet tier to
  // request, since a lone card gets the full container width while 2+ cards split it between them
  totalCards: number
}>()

const pageStore = usePageStore()
const { trackEventInDataLayer } = useEpgWidgetTracking()

const cardsSrcSetKey = computed(() => {
  if (props.totalCards <= 1) return 'bx-teaser-container--vod-epg-cards-single'
  if (props.totalCards === 2) return 'bx-teaser-container--vod-epg-cards-double'
  if (props.totalCards === 3) return 'bx-teaser-container--vod-epg-cards-triple'
  // a real 4-broadcast row settles into a permanently narrower plateau than a 3-broadcast row ever
  // reaches (see _default.ts) - needs its own tier, "triple" would overserve it at wide viewports
  return 'bx-teaser-container--vod-epg-cards-quad'
})

// Adapts an EPG-search broadcast + its channel into the composedTeaser shape BrandTeaser.vue expects, so the
// topic-page cards can reuse the exact same visual component as the `bx-teaser-container--vod-epg-slider`
// teasers in articles.
// genreName is passed as textLong so BrandTeaser can use its regular copytext handling.
const genreLabel = computed(() => props.broadcast.genreName || undefined)

const composedTeaser = computed(() => ({
  url: props.broadcast.url,
  titleLong: props.broadcast.title,
  textLong: genreLabel.value,
  airTime: props.broadcast.airTime,
  airTimeEnd: props.broadcast.airTimeEnd,
  hasAutoPlay: props.broadcast.hasAutoPlay,
  channelLogo: props.channel.logo
    ? { imageId: props.channel.logo.imageId, imageAltText: props.channel.logo.imageAltText || props.channel.name }
    : undefined,
  imageId: props.broadcast.imageLandscape?.imageId,
  mimeType: props.broadcast.imageLandscape?.mimetype,
  seoName: props.broadcast.imageLandscape?.seoName,
  imageCredit: props.broadcast.imageLandscape?.imageCredit,
  imageAltText: props.broadcast.imageLandscape?.imageAltText,
  imageIsAiGenerated: props.broadcast.imageLandscape?.isAiGenerated,
  croppings: { landscape: props.broadcast.imageLandscape?.crop }
}))

function handleClick () {
  trackEventInDataLayer('epg_program_click', { click_url: `${window.location.origin}${props.broadcast.url}` })
}
</script>

<template>
  <BrandTeaser :composed-teaser="composedTeaser"
               :meta-data="{ brand: pageStore.brandFromStore, teaserGroupType: cardsSrcSetKey, isEpgCard: true }"
               @click="handleClick" />
</template>
